import React, { useMemo } from "react";
import { Mafs, Polygon, Text, Theme } from "mafs";

/**
 * UnitConverter
 * - Aligns with Java UnitType @JsonValue symbols like "cm", "m", "km", "cm²", "m³", "mL", "L", "mg", "g", "kg", "t"
 * - Handles Unicode superscripts ² / ³
 * - Preserves inputValue=0 (uses ?? not ||)
 * - Supports arbitrary conversions across supported families by normalising to a base unit
 */

const DIM = {
    LINEAR: 1,
    AREA: 2,
    VOLUME: 3
};

// Map unit symbol -> metadata for conversion to base unit
// Base units chosen:
// - length: m
// - area: m²
// - volume: m³
// - capacity: L
// - mass: g
const UNIT_INDEX = {
    // Length (base: m)
    mm: { family: "length", dim: DIM.LINEAR, toBase: 0.001 },
    cm: { family: "length", dim: DIM.LINEAR, toBase: 0.01 },
    m: { family: "length", dim: DIM.LINEAR, toBase: 1 },
    km: { family: "length", dim: DIM.LINEAR, toBase: 1000 },

    // Area (base: m²)
    "cm²": { family: "area", dim: DIM.AREA, toBase: 0.0001 }, // (0.01 m)^2
    "m²": { family: "area", dim: DIM.AREA, toBase: 1 },

    // Volume (base: m³)
    "cm³": { family: "volume", dim: DIM.VOLUME, toBase: 1e-6 }, // (0.01 m)^3
    "m³": { family: "volume", dim: DIM.VOLUME, toBase: 1 },

    // Capacity (base: L)
    ml: { family: "capacity", dim: DIM.LINEAR, toBase: 0.001 },
    l: { family: "capacity", dim: DIM.LINEAR, toBase: 1 },

    // Mass (base: g)
    mg: { family: "mass", dim: DIM.LINEAR, toBase: 0.001 },
    g: { family: "mass", dim: DIM.LINEAR, toBase: 1 },
    kg: { family: "mass", dim: DIM.LINEAR, toBase: 1000 },
    t: { family: "mass", dim: DIM.LINEAR, toBase: 1_000_000 } // 1 tonne = 1,000,000 g
};

const normalizeUnitString = (u) => {
    if (u == null) return null;
    let s = String(u).trim();
    if (!s) return null;

    // Normalize whitespace + common variants
    s = s.replace(/\s+/g, "");

    // Keep unicode powers but convert common ASCII forms
    s = s
        .replace(/cm2/gi, "cm²")
        .replace(/m2/gi, "m²")
        .replace(/cm3/gi, "cm³")
        .replace(/m3/gi, "m³");

    // Case normalisation (backend uses "mL"/"L"; we store as lower for lookup)
    s = s.toLowerCase();

    // Capacity variants
    if (s === "millilitre" || s === "milliliter" || s === "millilitres" || s === "milliliters") s = "ml";
    if (s === "litre" || s === "liter" || s === "litres" || s === "liters") s = "l";

    // Mass variants
    if (s === "kilogram" || s === "kilograms") s = "kg";
    if (s === "gram" || s === "grams") s = "g";
    if (s === "milligram" || s === "milligrams") s = "mg";
    if (s === "tonne" || s === "tonnes" || s === "ton" || s === "tons") s = "t";

    // Length variants
    if (s === "metre" || s === "meter") s = "m";
    if (s === "kilometre" || s === "kilometer") s = "km";
    if (s === "centimetre" || s === "centimeter") s = "cm";
    if (s === "millimetre" || s === "millimeter") s = "mm";

    return s;
};

const safeNumber = (v, fallback) => {
    if (v === undefined || v === null) return fallback;
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return n;
};

const formatFactor = (f) => {
    if (!Number.isFinite(f)) return String(f);
    const abs = Math.abs(f);
    if (abs !== 0 && (abs < 0.001 || abs >= 10000)) return f.toExponential(3);
    return Number(f.toPrecision(6)).toString();
};

export const UnitConverter = ({ config = {} }) => {
    const fromUnit = normalizeUnitString(config.fromUnit ?? "m");
    const toUnit = normalizeUnitString(config.toUnit ?? "cm");

    // preserve 0; backend only fixes NaN/Infinity
    const inputValue = safeNumber(config.inputValue ?? 1.0, 1.0);

    const resolved = useMemo(() => {
        const fromMeta = UNIT_INDEX[fromUnit] || null;
        const toMeta = UNIT_INDEX[toUnit] || null;

        if (!fromMeta || !toMeta) {
            return {
                ok: false,
                reason: `Unsupported unit(s): ${String(config.fromUnit)} → ${String(config.toUnit)}`,
                fromUnit,
                toUnit,
                inputValue
            };
        }

        if (fromMeta.family !== toMeta.family || fromMeta.dim !== toMeta.dim) {
            return {
                ok: false,
                reason: `Incompatible units: ${fromUnit} → ${toUnit}`,
                fromUnit,
                toUnit,
                inputValue,
                fromMeta,
                toMeta
            };
        }

        // Convert via base: out = in * (from.toBase / to.toBase)
        const factor = fromMeta.toBase / toMeta.toBase;
        const outputValue = inputValue * factor;

        return { ok: true, fromUnit, toUnit, inputValue, outputValue, factor, meta: fromMeta };
    }, [fromUnit, toUnit, inputValue, config.fromUnit, config.toUnit]);

    const drawLinear = (value, unitLabel) => {
        const len = Math.max(0, value);
        const displayLen = Math.min(8, len);

        return (
            <>
                <Polygon
                    points={[
                        [0, 0],
                        [displayLen, 0],
                        [displayLen, 1],
                        [0, 1]
                    ]}
                    color={Theme.blue}
                    fillOpacity={0.2}
                    svgPolygonProps={{ stroke: "black", strokeWidth: 1 }}
                />
                <Text x={displayLen / 2} y={0.5} size={18} color="black">
                    {`${value} ${unitLabel}`}
                </Text>
            </>
        );
    };

    const drawArea = (value, unitLabel) => {
        const area = Math.max(0, value);
        const side = Math.sqrt(area);
        const displaySide = Math.min(6, side);

        return (
            <>
                <Polygon
                    points={[
                        [0, 0],
                        [displaySide, 0],
                        [displaySide, displaySide],
                        [0, displaySide]
                    ]}
                    color={Theme.green}
                    fillOpacity={0.2}
                    svgPolygonProps={{ stroke: "black", strokeWidth: 1 }}
                />
                <Text x={displaySide / 2} y={displaySide / 2} size={18} color="black">
                    {`${value} ${unitLabel}`}
                </Text>
            </>
        );
    };

    const drawVolume = (value, unitLabel) => {
        const vol = Math.max(0, value);
        const side = Math.cbrt(vol);
        const s = Math.min(5, side);

        const front = [
            [0, 0],
            [s, 0],
            [s, s],
            [0, s]
        ];
        const offset = s * 0.35;
        const top = [
            [0, s],
            [offset, s + offset],
            [s + offset, s + offset],
            [s, s]
        ];
        const sideFace = [
            [s, 0],
            [s + offset, offset],
            [s + offset, s + offset],
            [s, s]
        ];

        return (
            <>
                <Polygon points={front} color={Theme.orange} fillOpacity={0.15} svgPolygonProps={{ stroke: "black", strokeWidth: 1 }} />
                <Polygon points={top} color={Theme.orange} fillOpacity={0.10} svgPolygonProps={{ stroke: "black", strokeWidth: 1 }} />
                <Polygon points={sideFace} color={Theme.orange} fillOpacity={0.08} svgPolygonProps={{ stroke: "black", strokeWidth: 1 }} />

                <Text x={s / 2} y={s / 2} size={18} color="black">
                    {`${value} ${unitLabel}`}
                </Text>
            </>
        );
    };

    const renderVisual = (value, unitLabel, dim) => {
        if (dim === DIM.AREA) return drawArea(value, unitLabel);
        if (dim === DIM.VOLUME) return drawVolume(value, unitLabel);
        return drawLinear(value, unitLabel);
    };

    if (!resolved.ok) {
        return (
            <div style={{ padding: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Unit Converter</div>
                <div style={{ color: "#b91c1c", fontWeight: 600 }}>{resolved.reason}</div>
            </div>
        );
    }

    const { meta, factor, outputValue } = resolved;
    const dim = meta.dim;

    const viewBox =
        dim === DIM.VOLUME
            ? { x: [-2, 10], y: [-1, 10] }
            : dim === DIM.AREA
                ? { x: [-2, 10], y: [-2, 10] }
                : { x: [-1, 10], y: [-2, 4] };

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
                Convert {resolved.inputValue} {resolved.fromUnit} → {resolved.toUnit}
            </div>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 320px", minWidth: 300 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>From</div>
                    <Mafs viewBox={viewBox} pan={false}>
                        {renderVisual(resolved.inputValue, resolved.fromUnit, dim)}
                    </Mafs>
                </div>

                <div style={{ flex: "1 1 320px", minWidth: 300 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>To</div>
                    <Mafs viewBox={viewBox} pan={false}>
                        {renderVisual(outputValue, resolved.toUnit, dim)}
                    </Mafs>
                </div>
            </div>

            <div style={{ fontSize: 13, color: "rgba(15,23,42,0.85)" }}>
                <div>
                    <strong>Factor:</strong> × {formatFactor(factor)}
                </div>
                <div>
                    <strong>Working:</strong> {resolved.inputValue} × {formatFactor(factor)} = {Number.isFinite(outputValue) ? outputValue : String(outputValue)}
                </div>
                {dim > 1 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
                        Note: Area/volume factors are encoded in the unit symbols (e.g. cm², m³) and handled correctly.
                    </div>
                )}
            </div>
        </div>
    );
};
